import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { apiCall } from "utils/apiClient";
import type { Service } from "./types/booking.types";
import ReferenceImagesRow from "./components/ReferenceImagesRow";

type JobStatus =
  | "pending"
  | "assigned"
  | "in-progress"
  | "completed"
  | "cancelled"
  | "pending-acceptance"
  | "accepted"
  | "rejected"
  | string;

type PartnerStatus =
  | "not-assigned"
  | "pending-acceptance"
  | "accepted"
  | "rejected";

interface JobItem {
  id: number | string;
  serviceId: number | string;
  serviceName: string;
  quantity: number | string;
  unit: string;
  rate?: number | string;
  amount?: number | string;
  addonIds: Array<number | string>;
  addonNames: string[];
  addonItems?: {
    id: number | string;
    title: string;
    quantity: number | string;
    unit: string;
    rate?: number | string;
    amount?: number | string;
  }[];
}

interface JobDetailsPayload {
  id: number | string;
  jobNumber?: string;
  jobTitle?: string;
  jobItems: JobItem[];
  partnerName?: string;
  partnerNames?: string[];
  partnerIds?: number[];
  partnerStatus?: PartnerStatus | string;
  status?: JobStatus | string;
  scheduledDate?: string;
  completedDate?: string;
  notes?: string;
  totalPartnerAmount?: number | string;
  bookingId?: number | string;
}

const toNumber = (value: unknown, fallback = 0): number => {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toBool = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return value === "1" || value.toLowerCase() === "true";
  return false;
};

const parseReferenceImages = (referenceImage?: string | string[] | null): string[] => {
  if (!referenceImage) return [];
  if (Array.isArray(referenceImage)) {
    return referenceImage.filter(Boolean).map((image) => String(image));
  }
  if (typeof referenceImage === "string") {
    const trimmed = referenceImage.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean).map((image) => String(image));
      }
    } catch {
      return [trimmed];
    }
    return [trimmed];
  }
  return [];
};

const normalizeJobStatus = (status?: string): JobStatus => {
  if (!status) return "pending";

  const normalized = status.trim().toLowerCase().replace(/_/g, "-");
  if (!normalized) return "pending";

  switch (normalized) {
    case "inprogress":
      return "in-progress";
    case "assigned":
    case "in-progress":
    case "completed":
    case "cancelled":
    case "pending":
    case "pending-acceptance":
    case "accepted":
    case "rejected":
      return normalized;
    default:
      // Preserve unknown backend values instead of forcing "pending".
      return normalized;
  }
};

const normalizePartnerStatus = (status?: string): PartnerStatus => {
  if (!status) return "not-assigned";

  const normalized = status.trim().toLowerCase().replace(/_/g, "-");

  switch (normalized) {
    case "pending":
    case "pending-acceptance":
      return "pending-acceptance";
    case "accepted":
      return "accepted";
    case "rejected":
      return "rejected";
    case "not-assigned":
    case "unassigned":
      return "not-assigned";
    default:
      return "not-assigned";
  }
};

const getStatusColor = (status?: JobStatus) => {
  switch (status) {
    case "completed":
    case "accepted":
      return "bg-green-100 text-green-700 border-green-200";
    case "in-progress":
    case "assigned":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "pending-acceptance":
    case "pending":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    case "rejected":
    case "cancelled":
      return "bg-red-100 text-red-700 border-red-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const getPartnerStatusColor = (status?: PartnerStatus) => {
  switch (status) {
    case "accepted":
      return "bg-green-100 text-green-700 border-green-200";
    case "pending-acceptance":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    case "rejected":
      return "bg-red-100 text-red-700 border-red-200";
    case "not-assigned":
      return "bg-gray-100 text-gray-700 border-gray-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const getServiceStatusColor = (status?: string) => {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-700 border-green-200";
    case "completed":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "cancelled":
      return "bg-red-100 text-red-700 border-red-200";
    case "pending":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const JobDetailsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as { job?: JobDetailsPayload; bookingId?: number | string } | null;
  const jobFromState = state?.job;
  const [jobPayload, setJobPayload] = useState<JobDetailsPayload | null>(jobFromState ?? null);
  const [loadingJob, setLoadingJob] = useState(false);
  const [jobError, setJobError] = useState<string | null>(null);

  const bookingId = state?.bookingId ?? jobPayload?.bookingId ?? jobFromState?.bookingId;

  const [services, setServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [expandedServiceMedia, setExpandedServiceMedia] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (jobFromState) {
      setJobPayload(jobFromState);
    }
  }, [jobFromState]);

  useEffect(() => {
    if (!bookingId) return;

    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        const response: any = await apiCall({
          endpoint: `booking/admin/${bookingId}`,
          method: "GET",
        });

        if (response?.status === 200 && response.data) {
          const combined = [
            ...(response.data?.services || []),
            ...(response.data?.additional_services || []),
          ];
          setServices(combined);
        }
      } catch (err) {
        console.error("Failed to load booking services:", err);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, [bookingId]);

  const mapJobItems = (items: any[] = []): JobItem[] => {
    const safeItems = Array.isArray(items) ? items : [];
    const parentItems = safeItems.filter((item) => !item?.parent_item_id);

    return parentItems.map((item) => {
      const childItems = safeItems.filter((child) => child?.parent_item_id === item.id);
      return {
        id: Number(item.id) || item.id,
        serviceId: Number(item.source_id) || item.source_id,
        serviceName: item.title ?? item.service_name ?? "",
        quantity: item.quantity,
        unit: item.unit,
        rate: item.rate,
        amount: item.amount,
        addonIds: childItems.map((child) => Number(child?.source_id ?? child?.id) || child?.source_id || child?.id),
        addonNames: childItems.map((child) => child?.title || child?.name),
        addonItems: childItems.map((child) => ({
          id: child.id,
          title: child.title || child.name,
          quantity: child.quantity,
          unit: child.unit,
          rate: child.rate,
          amount: child.amount,
        })),
      };
    });
  };

  const normalizeApiJob = (apiJob: any, items: any[]): JobDetailsPayload => {
    return {
      id: apiJob?.id ?? apiJob?.job_id ?? apiJob?.jobId,
      jobNumber: apiJob?.job_id ?? apiJob?.job_number ?? apiJob?.job_code ?? apiJob?.job_no,
      jobTitle: apiJob?.title ?? apiJob?.job_title ?? apiJob?.service_title ?? "Job",
      jobItems: mapJobItems(items),
      bookingId:
        apiJob?.booking_id ??
        apiJob?.bookingId ??
        apiJob?.booking?.id ??
        apiJob?.booking?.booking_id,
      partnerName: apiJob?.partner_name ?? apiJob?.partnerName,
      partnerNames: apiJob?.partner_names ?? apiJob?.partnerNames,
      partnerIds:
        apiJob?.partner_ids ??
        apiJob?.partnerIds ??
        (apiJob?.partner_id ? [Number(apiJob.partner_id)] : undefined),
      partnerStatus: apiJob?.partner_status ?? apiJob?.partnerStatus,
      status: apiJob?.status ?? "pending",
      scheduledDate:
        apiJob?.estimated_start_date ??
        apiJob?.scheduled_date ??
        apiJob?.scheduledDate ??
        apiJob?.created_at,
      completedDate: apiJob?.completed_date ?? apiJob?.completedDate,
      notes: apiJob?.notes,
      totalPartnerAmount:
        apiJob?.total_partner_amount ??
        apiJob?.totalPartnerAmount ??
        apiJob?.amount ??
        apiJob?.total_amount,
    };
  };

  useEffect(() => {
    const jobId = jobFromState?.id ?? jobPayload?.id ?? (state as any)?.jobId ?? (state as any)?.job_id;
    if (!jobId) return;

    const fetchJobDetails = async () => {
      setLoadingJob(true);
      setJobError(null);
      try {
        const res: any = await apiCall({
          endpoint: `partner-jobs/${jobId}`,
          method: "GET",
        });

        if (res?.status === 200 && res.data) {
          const apiJob = res.data?.job ?? res.data;
          const apiItems = res.data?.items ?? [];
          const normalized = normalizeApiJob(apiJob, apiItems);
          setJobPayload(normalized);
        } else {
          setJobError(res?.message || "Failed to load job details");
        }
      } catch (err) {
        console.error("Failed to load job details:", err);
        setJobError("Failed to load job details");
      } finally {
        setLoadingJob(false);
      }
    };

    fetchJobDetails();
  }, [jobFromState, jobPayload?.id]);

  const job = useMemo(() => {
    if (!jobPayload) return null;

    const items = Array.isArray(jobPayload.jobItems)
      ? jobPayload.jobItems
      : [];
    const partnerName =
      jobPayload.partnerName ||
      (jobPayload.partnerNames && jobPayload.partnerNames.length > 0
        ? jobPayload.partnerNames[0]
        : jobPayload.partnerIds && jobPayload.partnerIds.length > 0
        ? `Partner #${jobPayload.partnerIds[0]}`
        : undefined);

    return {
      ...jobPayload,
      id: toNumber(jobPayload.id),
      jobNumber: jobPayload.jobNumber ? String(jobPayload.jobNumber) : String(jobPayload.id),
      jobTitle: jobPayload.jobTitle ? String(jobPayload.jobTitle) : "Job",
      partnerName,
      partnerStatus: normalizePartnerStatus(jobPayload.partnerStatus?.toString()),
      status: normalizeJobStatus(jobPayload.status?.toString()),
      jobItems: items.map((item) => ({
        ...item,
        id: toNumber(item.id),
        serviceId: toNumber(item.serviceId),
        serviceName: String(item.serviceName ?? ""),
        addonIds: item.addonIds.map((addonId) => toNumber(addonId)),
        addonNames: item.addonNames ?? [],
        addonItems: item.addonItems?.map((addonItem) => ({
          ...addonItem,
          title: String(addonItem.title ?? ""),
        })),
      })),
    };
  }, [jobPayload]);

  const calculateJobTotal = (data: JobDetailsPayload): number => {
    return data.jobItems.reduce((total, item) => {
      const service = services.find((s) => s.id === item.serviceId);
      const serviceRate = toNumber(item.rate ?? service?.partner_price);
      const serviceQty = toNumber(item.quantity);
      const serviceAmount = toNumber(item.amount, serviceRate * serviceQty);

      const addonsCostFromItems = (item.addonItems || []).reduce((addonTotal, addonItem) => {
        const addonRate = toNumber(addonItem.rate);
        const addonQty = toNumber(addonItem.quantity, 1);
        const addonAmount = toNumber(addonItem.amount, addonRate * addonQty);
        return addonTotal + addonAmount;
      }, 0);

      const addonsCostFromServices = item.addonIds.reduce<number>((addonTotal, addonId) => {
        const addon = service?.addons?.find((a) => a.id === addonId);
        if (!addon) return addonTotal;
        return addonTotal + toNumber(addon.partner_price) * toNumber(addon.quantity, 1);
      }, 0);

      const addonsCost =
        item.addonItems && item.addonItems.length > 0
          ? addonsCostFromItems
          : addonsCostFromServices;

      return total + serviceAmount + addonsCost;
    }, 0);
  };

  if (loadingJob && !job) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div className="mt-6 p-6 bg-white shadow rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800">Job Details</h3>
          <p className="text-sm text-gray-600 mt-2">Loading job details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!job) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div className="mt-6 p-6 bg-white shadow rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800">Job Details</h3>
          <p className="text-sm text-gray-600 mt-2">
            No job data found. Please open a job from Job Management.
          </p>
          {jobError && (
            <p className="text-sm text-red-600 mt-2">{jobError}</p>
          )}
          <button
            onClick={() => navigate("/job-management")}
            className="mt-4 px-4 py-2 text-sm bg-blue-600 text-white rounded"
          >
            Back to Jobs
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const jobTotal = toNumber(job.totalPartnerAmount, calculateJobTotal(job));
  const hasAssignedPartner =
    Boolean(job.partnerName) ||
    (Array.isArray(job.partnerNames) && job.partnerNames.length > 0) ||
    (Array.isArray(job.partnerIds) && job.partnerIds.length > 0);

  const partnerDisplayName = job.partnerName
    ? job.partnerName
    : Array.isArray(job.partnerNames) && job.partnerNames.length > 1
    ? `Multiple partners (${job.partnerNames.length})`
    : Array.isArray(job.partnerNames) && job.partnerNames.length === 1
    ? job.partnerNames[0]
    : Array.isArray(job.partnerIds) && job.partnerIds.length > 1
    ? `Multiple partners (${job.partnerIds.length})`
    : Array.isArray(job.partnerIds) && job.partnerIds.length === 1
    ? `Partner #${job.partnerIds[0]}`
    : "-";

  const partnerIdDisplay =
    Array.isArray(job.partnerIds) && job.partnerIds.length > 0
      ? job.partnerIds.join(", ")
      : "-";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="mt-6 p-6 bg-white shadow rounded-lg">
        <div className="flex items-center justify-between border-b pb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-800">{job.jobTitle}</h3>
            <p className="text-sm text-gray-500">{job.jobNumber}</p>
          </div>
          <button
            onClick={() => navigate("/job-management")}
            className="px-4 py-2 text-sm bg-gray-700 text-white rounded hover:bg-gray-800"
          >
            Back to Jobs
          </button>
        </div>

        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 mb-1">
                <span className="font-medium">Status:</span>
              </p>
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                  job.status
                )}`}
              >
                {job.status ? String(job.status).replace(/[-_]/g, " ").toUpperCase() : "PENDING"}
              </span>
            </div>
            <div>
              <p className="text-gray-600 mb-1">
                <span className="font-medium">Partner:</span>
              </p>
              {hasAssignedPartner ? (
                <div>
                  <p className="text-gray-900 text-sm">
                    <span className="font-medium">Name:</span> {partnerDisplayName}
                  </p>
                  <p className="text-gray-600 text-xs mt-1">
                    <span className="font-medium">ID:</span> {partnerIdDisplay}
                  </p>
                </div>
              ) : (
                <p className="text-orange-600 font-medium text-sm">Not Assigned</p>
              )}
            </div>
            <div>
              <p className="text-gray-600">
                <span className="font-medium">Scheduled Date:</span>
              </p>
              <p className="text-gray-900 text-sm">
                {job.scheduledDate ? new Date(job.scheduledDate).toLocaleDateString() : "-"}
              </p>
            </div>
            {job.completedDate && (
              <div>
                <p className="text-gray-600">
                  <span className="font-medium">Completed Date:</span>
                </p>
                <p className="text-gray-900 text-sm">
                  {new Date(job.completedDate).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
          {job.notes && (
            <div className="mt-3 pt-3 border-t border-gray-300">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Notes:</span> {job.notes}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-semibold text-gray-800">
              Services ({job.jobItems.length})
            </h4>
            {loadingServices && (
              <span className="text-xs text-gray-500">Loading service details...</span>
            )}
          </div>
          <div className="space-y-3">
            {job.jobItems.map((item) => {
              const service = services.find((s) => s.id === item.serviceId);
              const servicePartnerPrice = toNumber(item.rate ?? service?.partner_price);
              const serviceQty = toNumber(item.quantity);
              const serviceCost = toNumber(item.amount, servicePartnerPrice * serviceQty);
              const serviceHasMaterial = toBool(service?.with_material);
              const serviceStatus = service?.status ? String(service.status).toLowerCase() : undefined;
              const serviceStatusLabel = serviceStatus
                ? serviceStatus.replace("-", " ").toUpperCase()
                : "NOT SET";
              const serviceMedia = parseReferenceImages(service?.reference_image);
              const hasServiceMedia = serviceMedia.length > 0;
              const serviceKey = String(service?.id ?? item.serviceId ?? item.id);
              const isMediaExpanded = Boolean(expandedServiceMedia[serviceKey]);

              return (
                <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{item.serviceName}</p>
                      <div className="text-sm text-gray-600 mt-1 space-y-1">
                        <p>
                          Qty: <span className="font-semibold">{item.quantity} {item.unit}</span>
                        </p>
                        {servicePartnerPrice > 0 && (
                          <p className="text-xs text-gray-600">
                            Rate: <span className="font-semibold">{`\u20B9${servicePartnerPrice.toLocaleString("en-IN")}`}/{item.unit}</span>
                            <span className="mx-1">-</span>
                            <span className="text-gray-500">
                              {serviceHasMaterial ? "With Material" : "Without Material"}
                            </span>
                            <span className="mx-1">-</span>
                            Amount: {`\u20B9${serviceCost.toLocaleString("en-IN")}`} ({serviceQty} x {servicePartnerPrice.toLocaleString("en-IN")})
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {`\u20B9${serviceCost.toLocaleString("en-IN")}`}
                      </p>
                      <p className="text-xs text-gray-500">total</p>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-500">Status</span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${getServiceStatusColor(
                            serviceStatus
                          )}`}
                        >
                          {serviceStatusLabel}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedServiceMedia((prev) => ({
                              ...prev,
                              [serviceKey]: !prev[serviceKey],
                            }))
                          }
                          disabled={!hasServiceMedia}
                          className={`text-[11px] px-2 py-1 rounded border transition ${
                            hasServiceMedia
                              ? "bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
                              : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          }`}
                        >
                          View Media{hasServiceMedia ? ` (${serviceMedia.length})` : ""}
                        </button>
                      </div>
                    </div>
                  </div>

                  {isMediaExpanded && hasServiceMedia && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <ReferenceImagesRow
                        referenceImage={serviceMedia}
                        label="Service Media:"
                      />
                    </div>
                  )}

                  {(item.addonItems && item.addonItems.length > 0 ? item.addonItems.length : item.addonNames.length) > 0 && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <div className="space-y-2">
                        {(item.addonItems && item.addonItems.length > 0
                          ? item.addonItems
                          : item.addonNames.map((name, idx) => ({
                            id: item.addonIds[idx],
                            title: name,
                          })) as any).map((addonItem: any, idx: number) => {
                            const addonId = addonItem.id ?? item.addonIds[idx];
                            const addonObj = service?.addons?.find((a) => a.id === addonId);
                            const addonTitle = addonItem.title || addonItem.name || item.addonNames[idx];
                            const addonPartnerPrice = toNumber(addonItem.rate ?? addonObj?.partner_price);
                            const addonQuantity = toNumber(addonItem.quantity ?? addonObj?.quantity, 1);
                            const addonCost = toNumber(addonItem.amount, addonPartnerPrice * addonQuantity);
                            const addonHasMaterial = toBool(addonObj?.with_material);

                            return (
                              <div key={idx} className="bg-gray-50 rounded p-3 border border-gray-200">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <p className="font-semibold text-gray-800 text-sm">{addonTitle}</p>
                                    <div className="text-xs text-gray-600 mt-1 space-y-1">
                                      <p>
                                        Qty: <span className="font-semibold">{addonQuantity} {addonItem.unit ?? addonObj?.unit}</span>
                                      </p>
                                      {addonPartnerPrice > 0 && (
                                        <p className="text-xs text-gray-600">
                                          Rate: <span className="font-semibold">{`\u20B9${addonPartnerPrice.toLocaleString("en-IN")}`}/{addonItem.unit ?? addonObj?.unit}</span>
                                          <span className="mx-1">-</span>
                                          <span className="text-gray-500">
                                            {addonHasMaterial ? "With Material" : "Without Material"}
                                          </span>
                                          <span className="mx-1">-</span>
                                          Amount: {`\u20B9${addonCost.toLocaleString("en-IN")}`} ({addonQuantity} x {addonPartnerPrice.toLocaleString("en-IN")})
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs font-semibold text-gray-900">
                                      {`\u20B9${addonCost.toLocaleString("en-IN")}`}
                                    </p>
                                    <p className="text-[11px] text-gray-500">total</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6 border-t pt-4 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Job Total:
          </div>
          <div className="text-lg font-bold text-blue-600">
            {`\u20B9${jobTotal.toLocaleString("en-IN")}`}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default JobDetailsPage;
